import { useAccessControl } from './useAccessControl';
import { useUserAuth } from './auth/useUserAuth';
import { useCurrentUser } from './users/useCurrentUser';

export type AppRouteKey =
  | 'login'
  | 'dashboard'
  | 'staff'
  | 'select'
  | 'user'
  | 'invitations'
  | 'onboarding';

export const useAppNavigation = () => {
  const { authUser, isLoading: isAuthLoading } = useUserAuth();
  const { currentUser, isLoading: isUserLoading } = useCurrentUser();
  const { canAccessPage, isLoading: isAccessControlLoading } =
    useAccessControl();

  const isLoading = isAuthLoading || isAccessControlLoading || isUserLoading;

  const user = currentUser ?? authUser;

  const hasSelectedBusiness = !!user?.preferences?.selectedBusinessId;
  const isInvite = user?.source === 'invite';
  const hasOnboarded = !!user?.onboardingCompleted;

  const canAccessRoute = (routeKey: AppRouteKey): boolean => {
    // If not logged in, only login is accessible
    if (!authUser) {
      return routeKey === 'login';
    }

    // if logged in but user record is not loaded yet, wait. Wait, if currentUser is null, we can't do much.
    // However, isLoading handles the waiting phase.

    switch (routeKey) {
      case 'login':
        return false; // Already logged in
      case 'dashboard':
        return hasOnboarded && canAccessPage('dashboard'); // Business selection might be enforced by InterstitialGuard in Web
      case 'staff':
        return hasOnboarded && canAccessPage('staff');
      case 'select':
        return hasOnboarded && !hasSelectedBusiness; // Required to select
      case 'user':
        return hasOnboarded;
      case 'onboarding':
        return !hasOnboarded;
      case 'invitations':
        return isInvite || hasOnboarded;
      default:
        return false;
    }
  };

  const getFallbackRoute = (): AppRouteKey => {
    if (!authUser) return 'login';
    if (!hasOnboarded) return 'onboarding';

    // They are onboarded but haven't selected a business.
    // In web, InterstitialGuard handles this in-place, but Mobile uses /select.
    if (!hasSelectedBusiness) return 'select';

    // Business is selected or we're on a platform that handles it differently
    if (canAccessPage('dashboard')) return 'dashboard';
    if (canAccessPage('staff')) return 'staff';
    if (isInvite) return 'invitations';

    return 'user';
  };

  return {
    isLoading,
    canAccessRoute,
    getFallbackRoute,
  };
};
